import * as z from "zod";

/** Card brands a matched order's card company normalizes to (card_brand filter). */
export const disputeAlertCardBrands = [
  "visa",
  "mastercard",
  "amex",
  "discover",
] as const;
export type DisputeAlertCardBrand = (typeof disputeAlertCardBrands)[number];
export const DisputeAlertCardBrand = disputeAlertCardBrands;

/** Merchant-facing brand names, shared by the alert settings UI and analytics. */
export const disputeAlertCardBrandLabels: Record<
  DisputeAlertCardBrand,
  string
> = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "Amex",
  discover: "Discover",
};

/** Comparison for an amount filter against the (USD) dispute amount. */
export const disputeAlertAmountOperators = ["gte", "lte"] as const;
export type DisputeAlertAmountOperator =
  (typeof disputeAlertAmountOperators)[number];
export const DisputeAlertAmountOperator = disputeAlertAmountOperators;

export const disputeAlertFilterTypes = [
  "amount",
  "descriptor",
  "card_brand",
  "card_bin",
] as const;
export type DisputeAlertFilterType = (typeof disputeAlertFilterTypes)[number];
export const DisputeAlertFilterType = disputeAlertFilterTypes;

/**
 * A stored string-array filter value (descriptors / BINs). Mongoose stores these
 * loosely (nullable, possibly with null/blank entries); parsing drops null/blank
 * entries and requires at least one value to remain.
 */
const disputeAlertStringValues = z.preprocess(
  (value) =>
    Array.isArray(value)
      ? value.filter(
          (item): item is string =>
            typeof item === "string" && item.trim().length > 0,
        )
      : value,
  z.array(z.string()).min(1),
);

/** A stored card-brand filter value; unrecognized brands are dropped on parse. */
const disputeAlertBrandValues = z.preprocess(
  (value) =>
    Array.isArray(value)
      ? value.filter((brand) =>
          (disputeAlertCardBrands as readonly string[]).includes(
            brand as string,
          ),
        )
      : value,
  z.array(z.enum(disputeAlertCardBrands)).min(1),
);

/**
 * A single auto-refund filter, matched against the alert (and, for `card_brand`,
 * the matched order). A filter's multiple values are OR'd — the alert matches
 * the filter if it equals ANY listed value.
 *
 * - `amount`: the (USD) dispute amount compared via `operator` to `amountUsd`.
 * - `descriptor`: the alert's statement descriptor equals one of `descriptors`
 *   (case-insensitive).
 * - `card_brand`: the matched order's card brand is one of `brands`.
 * - `card_bin`: the alert's card BIN is one of `bins`.
 *
 * This schema is the single source of truth for the filter shape: the domain
 * type is inferred from it, the read path parses stored filters through it
 * (dropping malformed ones), and the engine/write validator narrow against it.
 * It validates shape only — business rules (non-negative amount, BIN format)
 * live in `validateAlertAutomationSettings` (below), so a structurally-valid but
 * out-of-policy stored filter still parses (and simply won't match at runtime).
 */
export const DisputeAlertFilter = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("amount"),
    operator: z.enum(disputeAlertAmountOperators),
    amountUsd: z.number(),
  }),
  z.object({
    type: z.literal("descriptor"),
    descriptors: disputeAlertStringValues,
  }),
  z.object({ type: z.literal("card_brand"), brands: disputeAlertBrandValues }),
  z.object({ type: z.literal("card_bin"), bins: disputeAlertStringValues }),
]);
export type DisputeAlertFilter = z.infer<typeof DisputeAlertFilter>;

/** A set of filters that must ALL match (AND). */
export const DisputeAlertFilterGroup = z.object({
  // At least one filter: an empty group is a vacuous AND (matches everything),
  // so a well-formed group must list >=1 filter. Redundant with the read-side
  // normalizer (drops groups with no valid filters) and the write validator
  // (rejects empty groups); this just makes the invariant explicit at the schema.
  filters: z.array(DisputeAlertFilter).min(1),
});
export type DisputeAlertFilterGroup = z.infer<typeof DisputeAlertFilterGroup>;

/**
 * Auto-refund rules for a single alert network (CDRN or ETHOCA). A matched alert
 * is auto-refunded when it satisfies ANY group (OR across groups); within a
 * group every filter must match (AND). No groups configured = auto-refund every
 * matched alert (the default posture). Automation only ever refunds — there is
 * no auto-decline.
 */
export const NetworkAlertRules = z.object({
  refundGroups: z.array(DisputeAlertFilterGroup).optional(),
});
export type NetworkAlertRules = z.infer<typeof NetworkAlertRules>;

/**
 * Merchant-configured automation for pre-dispute alerts, scoped per network so
 * CDRN and ETHOCA are configured independently. RDR is never automated, so it
 * has no rules block.
 */
export const DisputeAlertAutomationSettings = z.object({
  CDRN: NetworkAlertRules.optional(),
  ETHOCA: NetworkAlertRules.optional(),
});
export type DisputeAlertAutomationSettings = z.infer<
  typeof DisputeAlertAutomationSettings
>;

/**
 * Settings-write validation, shared by the merchant settings form (client-side)
 * and the update RPC handler (server-side). Lives here in the browser-safe
 * settings package so both run identical checks; the dispute service re-exports
 * it for server callers. Validates each network's filter groups independently.
 * The decision engine degrades gracefully on malformed stored data (a bad filter
 * simply doesn't match), so this is a write-time guard, not a runtime dependency.
 */
export function validateAlertAutomationSettings(
  rules: DisputeAlertAutomationSettings,
): string[] {
  const errors: string[] = [];
  for (const networkKey of ["CDRN", "ETHOCA"] as const) {
    const groups = rules[networkKey]?.refundGroups;
    if (!groups) {
      continue;
    }
    for (const [index, group] of groups.entries()) {
      errors.push(
        ...validateGroup(group).map(
          (error) => `${networkKey} group ${index + 1}: ${error}`,
        ),
      );
    }
  }
  return errors;
}

/**
 * Normalize a settings payload for STORAGE: trim each descriptor / card_bin value
 * and drop empties, so a whitespace-padded entry (e.g. `" 123456 "`) is never
 * persisted verbatim. Shared by the form + the update handler; apply before
 * validating + storing. Pure; amount / card_brand filters pass through unchanged.
 */
export function normalizeAlertAutomationSettings(
  rules: DisputeAlertAutomationSettings,
): DisputeAlertAutomationSettings {
  const normalizeFilter = (filter: DisputeAlertFilter): DisputeAlertFilter => {
    switch (filter.type) {
      case "descriptor":
        return {
          ...filter,
          descriptors: filter.descriptors.map((v) => v.trim()).filter(Boolean),
        };
      case "card_bin":
        return {
          ...filter,
          bins: filter.bins.map((v) => v.trim()).filter(Boolean),
        };
      default:
        return filter;
    }
  };
  const normalizeNetwork = (
    network: NetworkAlertRules | undefined,
  ): NetworkAlertRules | undefined =>
    network?.refundGroups
      ? {
          ...network,
          refundGroups: network.refundGroups.map((group) => ({
            ...group,
            filters: group.filters.map(normalizeFilter),
          })),
        }
      : network;
  return {
    CDRN: normalizeNetwork(rules.CDRN),
    ETHOCA: normalizeNetwork(rules.ETHOCA),
  };
}

function validateGroup(group: DisputeAlertFilterGroup): string[] {
  if (group.filters.length === 0) {
    return ["a group must contain at least one filter"];
  }
  return [
    ...duplicateFilterErrors(group),
    ...group.filters.flatMap(validateFilter),
  ];
}

/** Membership filters that AND to a nonsensical intersection if repeated. */
const SINGLE_PER_GROUP_TYPES = [
  "descriptor",
  "card_brand",
  "card_bin",
] as const;

/**
 * A group AND-s its filters, so two membership filters of the same type intersect
 * — e.g. two `card_bin` filters can never both match a single BIN, so the group
 * silently never matches. Allow at most one descriptor / card_brand / card_bin per
 * group; list multiple values in that filter's array instead. `amount` is the
 * exception: a gte plus an lte express a range, so at most one of EACH operator is
 * allowed (never two gte or two lte).
 */
function duplicateFilterErrors(group: DisputeAlertFilterGroup): string[] {
  const errors: string[] = [];
  for (const type of SINGLE_PER_GROUP_TYPES) {
    if (group.filters.filter((filter) => filter.type === type).length > 1) {
      errors.push(`a group may have at most one ${type} filter`);
    }
  }
  for (const operator of ["gte", "lte"] as const) {
    const count = group.filters.filter(
      (filter) => filter.type === "amount" && filter.operator === operator,
    ).length;
    if (count > 1) {
      errors.push(`a group may have at most one amount ${operator} filter`);
    }
  }
  // A gte above the lte is an empty range that can never match — reject it so
  // merchants don't save a dead rule that silently never fires.
  const gte = group.filters.find(
    (filter) => filter.type === "amount" && filter.operator === "gte",
  );
  const lte = group.filters.find(
    (filter) => filter.type === "amount" && filter.operator === "lte",
  );
  if (
    gte?.type === "amount" &&
    lte?.type === "amount" &&
    gte.amountUsd > lte.amountUsd
  ) {
    errors.push("amount range is inverted: gte must not be greater than lte");
  }
  return errors;
}

function validateFilter(filter: DisputeAlertFilter): string[] {
  switch (filter.type) {
    case "amount":
      return Number.isFinite(filter.amountUsd) && filter.amountUsd >= 0
        ? []
        : ["amount must be a non-negative number"];
    case "descriptor":
      if (filter.descriptors.length === 0) {
        return ["descriptor filter needs at least one descriptor"];
      }
      return filter.descriptors.some((value) => !value.trim())
        ? ["descriptors must be non-empty"]
        : [];
    case "card_brand":
      return filter.brands.length === 0
        ? ["card_brand filter needs at least one brand"]
        : [];
    case "card_bin":
      if (filter.bins.length === 0) {
        return ["card_bin filter needs at least one BIN"];
      }
      return filter.bins.some((value) => !/^\d{6,8}$/.test(value.trim()))
        ? ["BINs must be 6-8 digits"]
        : [];
  }
}
